import type {ReactNode} from 'react';

import {
  ChatEndedRow,
  MetaEventRow,
  ParticipantJoinedRow,
  ParticipantLeftRow,
  TranslationRow,
} from '@stripe-internal/conversations-ui';
import type {
  Conversation,
  ConversationLogItem,
  ConversationMessage,
  TextConversationMessage,
} from '@stripe-internal/conversations-client';

import {GroupedMessagesRow} from './GroupedMessagesRow';
import {JoinRow} from './Chat/JoinRow';
import {MetaRow} from './Chat/MetaRow';

type GroupingOptions = {
  animated?: boolean;
  disableInteraction?: boolean;
};

const groupedMessageTypes = new Set<ConversationMessage['type']>([
  'text-conversation-message',
  'rich-text-conversation-message',
  'attachment-conversation-message',
  'inline-data-conversation-message',
]);

function isTextConversationMessage(
  message: ConversationMessage,
): message is TextConversationMessage {
  return message.type === 'text-conversation-message';
}

export function renderItemsGroupedV1(
  conversation: Conversation,
  items: ConversationLogItem[],
  {animated, disableInteraction}: GroupingOptions,
) {
  const rows: ReactNode[] = [];

  let translationRowAdded = false;
  let conversationParticipantJoined = false;

  for (let i = 0; i < items.length; i += 1) {
    const item = items[i];

    switch (item.name) {
      case 'conversation-participant-joined': {
        conversationParticipantJoined = true;
        rows.push(
          <ParticipantJoinedRow
            key={item.id}
            conversation={conversation}
            item={item}
            JoinRowComponent={JoinRow}
          />,
        );
        break;
      }
      case 'conversation-participant-left': {
        rows.push(
          <ParticipantLeftRow
            key={item.id}
            conversation={conversation}
            item={item}
            MetaRowComponent={MetaRow}
          />,
        );
        break;
      }
      case 'conversation-message-added': {
        const participantMessages = [item];
        const {participant} = item.message;

        const isTranslationCase =
          isTextConversationMessage(item.message) &&
          !!item.message.originalText;

        if (
          isTranslationCase &&
          !translationRowAdded &&
          conversationParticipantJoined
        ) {
          rows.push(<TranslationRow MetaRowComponent={MetaRow} />);
          translationRowAdded = true;
        }

        for (let j = i + 1; j < items.length; j += 1) {
          const nextItem = items[j];

          if (nextItem.name === 'conversation-message-added') {
            const isGroupedType =
              nextItem.message.participant.id === participant.id &&
              groupedMessageTypes.has(nextItem.message.type);

            if (isGroupedType) {
              participantMessages.push(nextItem);
              i = j;

              // eslint-disable-next-line no-continue
              continue;
            }
          }

          break;
        }

        rows.push(
          <GroupedMessagesRow
            key={`grouped-${item.id}`}
            conversation={conversation}
            items={participantMessages}
            disableInteraction={disableInteraction}
            animated={animated}
          />,
        );
        break;
      }
      case 'conversation-meta': {
        rows.push(
          <MetaEventRow key={item.id} item={item} MetaRowComponent={MetaRow} />,
        );
        break;
      }
      case 'conversation-ended': {
        if (!conversation.context.surpressChatEndedNotice) {
          rows.push(<ChatEndedRow key={item.id} MetaRowComponent={MetaRow} />);
        }
        break;
      }
      case 'conversation-custom-component':
        // Not emitted by the audited dot-com V1 forest.
        break;
      default:
        break;
    }
  }

  return rows;
}
